import { EventEmitter, h, Component, Host, Element, Prop, Watch, Listen, Event } from '@stencil/core';

import type { TableColumnInterface, TableStateInterface } from '../../../services/interfaces';
import { TableOrderInterface, BoxRoundedPreset } from '../../../services/interfaces';
import { createUUID } from '../../../services/utils';

import { setTableState, getTableState, removeTableState } from './helpers/table-state';

@Component({
  tag: 'pk-table',
  styleUrl: './scss/pk-table.scss',
  scoped: true,
})
export class PkTable {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkTableElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Changes the cell top and bottom padding. Minimum is 4px, and must be a multiple of 4. */
  @Prop({ reflect: true }) cellVerticalPad?: number = 4;

  /** Changes the cell left and right padding. Minimum is 8px, and must be a multiple of 4. */
  @Prop({ reflect: true }) cellHorizontalPad?: number = 8;

  /**
   * Changes the margin between individual rows. Minimum is 0, must be a multiple of 8.
      Using rowSpacing > 8 will result in box-shadow on rows.
      Using rowSpacing === 0 will result in row border-radius of 0.
   */
  @Prop({ reflect: true }) rowSpacing?: number = 8;

  /** If true, applies horizontal divider between rows. Can ONLY be used when rowSpacing is 0.*/
  @Prop({ reflect: true }) rowDivider?: boolean;

  /** Specify the value of the row corner radius
   * (it should be a valid Panenka --border-radius token value) */
  @Prop({ reflect: true }) rowRounded: BoxRoundedPreset = 'xl';

  /** If `true`, a box shadow will applied to the row container */
  @Prop({ reflect: true }) rowShadow: boolean = false;

  /** If `false`, the row stroke border style will be removed */
  @Prop({ reflect: true }) rowStroke: boolean = true;

  /** If true, visually hides the table head with "display: none". The head is still needed to set sizes on the columns/cells */
  @Prop({ reflect: true }) noHeader?: boolean;

  /** If true, the first cell in a row will become the row header under 768px width. */
  @Prop({ reflect: true }) firstCellMobileHeader?: boolean;

  /** If true, the row won't be set as selected and it won't trigger any action when is clicked. */
  @Prop({ reflect: true }) disableClick: boolean = true;

  /**
   * If true, the table `width` will be fixed (in px) instead of taking 100% of its parent container.
   * In case the table width is bigger than its container, the horizontal scrolling will be available.
   * Please notice that you cannot use `useFixedWidth="true"` combined with `usePercentageWidth="true"` on the pk-table-header-col.
   */
  @Prop() useFixedWidth: boolean;

  /** If true, an overlay loader will be shown on the table body instead of the rows. */
  @Prop({ reflect: true }) loading: boolean;

  /** Table unique identifier. */
  @Prop({ reflect: true }) tableId: string = createUUID();

  /** It will set the initial sort column of the table. */
  @Prop({ reflect: true, mutable: true }) sortBy: TableColumnInterface['columnKey'] = null;

  /** Set the initial order of the sorting sequence. */
  @Prop({ reflect: true, mutable: true }) sortOrder: TableOrderInterface = 'DESC';

  /** The width of the table, either on pixels (px) or percent (%). */
  @Prop() width: string = '100%';

  // Prop lifecycle events
  // =======================

  @Watch('cellVerticalPad')
  @Watch('cellHorizontalPad')
  @Watch('rowSpacing')
  @Watch('rowDivider')
  @Watch('rowRounded')
  @Watch('rowShadow')
  @Watch('rowStroke')
  @Watch('noHeader')
  @Watch('firstCellMobileHeader')
  @Watch('disableClick')
  @Watch('useFixedWidth')
  @Watch('loading')
  @Watch('sortOrder')
  @Watch('sortBy')
  toogleLoading() {
    const tableState: TableStateInterface = getTableState(this.tableId);
    if (!tableState) return;

    tableState.cellVerticalPad = this.cellVerticalPad;
    tableState.cellHorizontalPad = this.cellHorizontalPad;
    tableState.rowSpacing = this.rowSpacing;
    tableState.rowDivider = this.rowDivider;
    tableState.rowRounded = this.rowRounded;
    tableState.rowShadow = this.rowShadow;
    tableState.rowStroke = this.rowStroke;
    tableState.noHeader = this.noHeader;
    tableState.firstCellMobileHeader = this.firstCellMobileHeader;
    tableState.disableClick = this.disableClick;
    tableState.useFixedWidth = this.useFixedWidth;
    tableState.isLoading = this.loading;
    tableState.sortBy = { order: this.sortOrder, columnKey: this.sortBy };
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the sort table change */
  @Event() pkTableSortBy: EventEmitter<{ columnKey: TableColumnInterface['columnKey']; order: TableOrderInterface }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  async componentWillLoad() {
    const tableState: TableStateInterface = {
      cellVerticalPad: this.cellVerticalPad,
      cellHorizontalPad: this.cellHorizontalPad,
      rowSpacing: this.rowSpacing,
      rowDivider: this.rowDivider,
      rowRounded: this.rowRounded,
      rowShadow: this.rowShadow,
      rowStroke: this.rowStroke,
      noHeader: this.noHeader,
      firstCellMobileHeader: this.firstCellMobileHeader,
      cols: [],
      disableClick: this.disableClick,
      useFixedWidth: this.useFixedWidth,
      id: this.tableId,
      isLoading: this.loading,
      sortBy: { order: this.sortOrder, columnKey: this.sortBy },
    };
    setTableState(this.tableId, tableState);
  }

  componentDidLoad() {
    this.toogleLoading();
  }

  disconnectedCallback() {
    removeTableState(this.tableId);
  }

  // Listeners
  // ==============
  @Listen('pkTableHeaderColClick')
  triggerSortEvent(ev?: UIEvent) {
    const { columnKey, order } = ev.detail as unknown as {
      columnKey: TableColumnInterface['columnKey'];
      order: TableOrderInterface;
    };
    this.sortBy = columnKey;
    this.sortOrder = order;
    this.pkTableSortBy.emit({ columnKey: this.sortBy, order: this.sortOrder });
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // ==========================

  // 1render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host
        style={{
          width: this.width,
        }}
        class="pk-table"
        role="table"
      >
        <slot />
      </Host>
    );
  }
}
